import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Dropdown, Space, Checkbox, AutoComplete } from 'antd';
import { UserOutlined, LockOutlined, DatabaseOutlined, GlobalOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../hooks/useI18n';
import type { MenuProps } from 'antd';

const { Title } = Typography;

const LOGIN_HISTORY_KEY = 'loginHistory';

interface LoginIdentity {
  username: string;
  tenantCode: string;
}

const Login: React.FC = () => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t, currentLanguage, setCurrentLanguage, supportedLanguages } = useI18n();
  const [, forceUpdate] = useState({});
  const [rememberPassword, setRememberPassword] = useState(false);
  const [loginHistory, setLoginHistory] = useState<LoginIdentity[]>([]);

  // 监听语言变化事件，自动重新渲染组件
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // 加载保存的登录信息
  useEffect(() => {
    // 兼容旧的记住密码逻辑（包含密码）
    const savedCredentials = localStorage.getItem('savedCredentials');
    if (savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        form.setFieldsValue({
          username: credentials.username,
          password: credentials.password,
          tenantCode: credentials.tenantCode
        });
        setRememberPassword(true);
        return;
      } catch (error) {
        console.error('Failed to load saved credentials:', error);
        localStorage.removeItem('savedCredentials');
      }
    }

    // 新增：始终记住最近一次登录的 Tenant Code 和用户名（不包含密码）
    const savedIdentity = localStorage.getItem('savedLoginIdentity');
    if (savedIdentity) {
      try {
        const identity = JSON.parse(savedIdentity);
        form.setFieldsValue({
          username: identity.username,
          tenantCode: identity.tenantCode
        });
      } catch (error) {
        console.error('Failed to load saved login identity:', error);
        localStorage.removeItem('savedLoginIdentity');
      }
    }

    // 加载多条历史记录，用于下拉选择
    const historyRaw = localStorage.getItem(LOGIN_HISTORY_KEY);
    if (historyRaw) {
      try {
        const history: LoginIdentity[] = JSON.parse(historyRaw);
        if (Array.isArray(history)) {
          setLoginHistory(history);
        }
      } catch (error) {
        console.error('Failed to load login history:', error);
        localStorage.removeItem(LOGIN_HISTORY_KEY);
      }
    }
  }, [form]);

  // 语言切换处理
  const handleLanguageSelect = (languageCode: string) => {
    setCurrentLanguage(languageCode);
    message.success(t('common.languageChanged', { language: supportedLanguages.find(lang => lang.code === languageCode)?.name || languageCode }));
  };

  // 语言菜单项
  const languageMenuItems: MenuProps['items'] = supportedLanguages.map(language => ({
    key: language.code,
    label: language.name,
    onClick: () => handleLanguageSelect(language.code),
  }));

  const handleLogin = async (values: { username: string; password: string; tenantCode?: string }) => {
    const result = await login(values.username, values.password, values.tenantCode);
    
    if (result.success) {
      // 登录成功，处理记住密码功能
      if (rememberPassword) {
        // 保存登录信息到本地存储
        const credentials = {
          username: values.username,
          password: values.password,
          tenantCode: values.tenantCode || 'default'
        };
        localStorage.setItem('savedCredentials', JSON.stringify(credentials));
      } else {
        // 不记住密码，清除已保存的信息
        localStorage.removeItem('savedCredentials');
      }

      // 无论是否记住密码，都记住最近一次使用的 Tenant Code 和用户名（不保存密码）
      const identity: LoginIdentity = {
        username: values.username,
        tenantCode: values.tenantCode || 'default'
      };
      localStorage.setItem('savedLoginIdentity', JSON.stringify(identity));

      // 更新本地多条历史记录（用于下拉），最多保留 10 条
      setLoginHistory(prev => {
        const filtered = prev.filter(
          item => !(item.username === identity.username && item.tenantCode === identity.tenantCode)
        );
        const next = [identity, ...filtered].slice(0, 10);
        localStorage.setItem(LOGIN_HISTORY_KEY, JSON.stringify(next));
        return next;
      });
      
      message.success(t('login.loginSuccess', { username: values.username }));
      navigate('/');
    } else {
      // 显示详细的错误信息
      const errorMessage = result.error || t('login.loginFailed');
      message.error(errorMessage);
      // 登录失败时不清空表单内容
    }
  };

  const tenantOptions = Array.from(
    new Set(loginHistory.map(item => item.tenantCode).filter(Boolean))
  ).map(value => ({ value }));

  const usernameOptions = Array.from(
    new Set(loginHistory.map(item => item.username).filter(Boolean))
  ).map(value => ({ value }));

  return (
    <div className="login-container" style={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      {/* 语言切换按钮 */}
      <div style={{ 
        position: 'absolute', 
        top: '20px', 
        right: '20px',
        zIndex: 1000
      }}>
        <Dropdown 
          menu={{ items: languageMenuItems }} 
          placement="bottomRight"
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<GlobalOutlined />}
            style={{ 
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px'
            }}
          >
            {supportedLanguages.find(lang => lang.code === currentLanguage)?.name || 'Language'}
          </Button>
        </Dropdown>
      </div>

      <Card style={{ 
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        border: 'none'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>{t('login.title')}</Title>
          <Typography.Text type="secondary">{t('login.subtitle')}</Typography.Text>
        </div>
        
        <Form
          form={form}
          name="login"
          onFinish={handleLogin}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="tenantCode"
            label={t('login.tenantCode')}
            rules={[{ required: true, message: t('login.tenantCodeRequired') }]}
          >
            <AutoComplete
              options={tenantOptions}
              style={{ width: '100%' }}
            >
              <Input
                prefix={<DatabaseOutlined />}
                placeholder={t('login.tenantCodePlaceholder')}
                size="large"
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            name="username"
            rules={[{ required: true, message: t('login.usernameRequired') }]}
          >
            <AutoComplete
              options={usernameOptions}
              style={{ width: '100%' }}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('login.usernamePlaceholder')}
                size="large"
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: t('login.passwordRequired') }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('login.passwordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={rememberPassword}
              onChange={(e) => setRememberPassword(e.target.checked)}
            >
              {t('login.rememberPassword')}
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
            >
              {t('login.login')}
            </Button>
          </Form.Item>
        </Form>

      </Card>
    </div>
  );
};

export default Login;
